import { useCallback } from 'react';
import { useAuth } from '@/hooks/auth/useAuth';

/**
 * Re-read `/auth/me` after a mutation that may have changed the caller's own
 * membership, roles, or teams.
 *
 * **Why a hook and not `invalidateQueries`.** `useAuth` holds the session in
 * a `useReducer`, not in React Query. Nothing queries the `['auth', 'me']`
 * key, so an `invalidateQueries` against it marks nothing stale and refetches
 * nothing — every such call in this app was a silent no-op.
 * `refreshSession()` is the only thing that re-reads the session.
 *
 * **Why it matters.** `activeMembership` is what `can()` gates every write
 * affordance on and what `useMyTeams` (and therefore every owner picker)
 * reads. Change your own role, join or leave a team, rename a team, or
 * remove yourself from an org, and without this the tab keeps gating on the
 * snapshot it took at mount until a hard reload.
 *
 * Use {@link useRefreshSessionIfSelf} instead on surfaces that act on an
 * arbitrary user (the platform-admin org panel), where an unconditional
 * re-read would fire `/auth/me` for actions against orgs the caller has
 * nothing to do with.
 */
export function useRefreshSession(): () => void {
  const { refreshSession } = useAuth();
  return useCallback(() => {
    void refreshSession();
  }, [refreshSession]);
}

/**
 * {@link useRefreshSession}, but only when the mutation targeted the caller.
 *
 * For admin surfaces that operate on an arbitrary tenant: the one case where
 * the caller's own `memberships[].permissions` just changed is when they were
 * the target.
 */
export function useRefreshSessionIfSelf(): (userId: string) => void {
  const { refreshSession, user } = useAuth();
  const selfId = user?.id;
  return useCallback(
    (userId: string) => {
      if (userId === selfId) void refreshSession();
    },
    [refreshSession, selfId],
  );
}
